import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Phone, MapPin, ArrowRight, GraduationCap,
  Sun, Droplets, Award, Loader2, Menu, X, Check,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);

const BRAND_ICON = { Veloskin: ShieldCheck, Velotint: Sun, VeloguardX: Droplets };

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const injectJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export default function Landing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const enquiryRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/public/landing");
        if (alive) setData(d);
      } catch (e) {
        /* public page still renders static shell */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const seo = data.content?.seo || {};
    document.title = seo.title || "Veloskin — Glossiest Paint Protection Film in India";
    setMeta("description", seo.description);
    setMeta("og:title", seo.title, "property");
    setMeta("og:description", seo.description, "property");
    setMeta("og:type", "website", "property");
    const heroImg = mediaUrl(data.content?.hero?.image);
    if (heroImg) setMeta("og:image", heroImg, "property");

    injectJsonLd("ld-org", {
      "@context": "https://schema.org",
      "@type": "AutomotiveBusiness",
      name: "Veloskin PPF",
      description: seo.description,
      image: heroImg || undefined,
      url: "https://www.veloskinppf.com",
      telephone: (data.content?.hero?.phones || [])[0],
      areaServed: "India",
      makesOffer: (data.products || []).map((p) => ({
        "@type": "Offer", itemOffered: { "@type": "Product", name: p.name, description: p.description },
      })),
    });
    injectJsonLd("ld-detailers", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: (data.detailers || []).map((d, i) => ({
        "@type": "ListItem", position: i + 1,
        item: {
          "@type": "AutomotiveBusiness", name: d.name,
          address: d.city ? { "@type": "PostalAddress", addressLocality: d.city, addressCountry: "IN" } : undefined,
          telephone: d.phone || undefined, url: d.maps_url || undefined,
        },
      })),
    });
  }, [data]);

  const scrollToEnquiry = () => enquiryRef.current?.scrollIntoView({ behavior: "smooth" });

  const content = data?.content || {};
  const hero = content.hero || {};
  const academy = content.academy || {};
  const products = data?.products || [];
  const services = data?.services || [];
  const detailers = data?.detailers || [];
  const company = content.company || {};

  return (
    <div className="min-h-screen bg-[#1B0730] text-white" data-testid="landing-page">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#1B0730]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="#top" className="flex items-center gap-2.5" data-testid="landing-logo">
            {hero.logo ? (
              <img src={mediaUrl(hero.logo)} alt="Veloskin PPF" className="h-8 w-auto sm:h-9" />
            ) : (
              <span className="font-display text-xl font-bold tracking-tight">
                VELOSKIN <span className="text-crimson-500">PPF</span>
              </span>
            )}
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            <a href="#products" className="text-sm font-medium text-white/70 transition-colors hover:text-white">Products</a>
            <a href="#academy" className="text-sm font-medium text-white/70 transition-colors hover:text-white">Academy</a>
            <a href="#detailers" className="text-sm font-medium text-white/70 transition-colors hover:text-white">Detailers</a>
            <a href="#enquiry" className="text-sm font-medium text-white/70 transition-colors hover:text-white">Contact</a>
            <Link
              to="/login"
              data-testid="landing-login-btn"
              className="rounded-full bg-crimson-500 px-5 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(245,24,91,0.8)] transition-transform duration-200 hover:scale-105"
            >
              Login
            </Link>
          </nav>
          <button className="md:hidden" onClick={() => setMenuOpen((v) => !v)} data-testid="landing-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="border-t border-white/10 px-5 py-4 md:hidden" data-testid="landing-mobile-menu">
            <div className="flex flex-col gap-3">
              <a href="#products" onClick={() => setMenuOpen(false)} className="text-sm text-white/80">Products</a>
              <a href="#academy" onClick={() => setMenuOpen(false)} className="text-sm text-white/80">Academy</a>
              <a href="#detailers" onClick={() => setMenuOpen(false)} className="text-sm text-white/80">Detailers</a>
              <a href="#enquiry" onClick={() => setMenuOpen(false)} className="text-sm text-white/80">Contact</a>
              <Link to="/login" data-testid="landing-login-btn-mobile" className="rounded-full bg-crimson-500 px-5 py-2 text-center text-sm font-semibold">Login</Link>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[32rem] w-[32rem] rounded-full bg-crimson-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[32rem] w-[32rem] rounded-full bg-brand/40 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
          <div className="fade-in">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-crimson-500/40 bg-crimson-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-crimson-200">
              <Sparkles className="h-3.5 w-3.5" /> German TPU Technology
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              {hero.headline || "The Glossiest Paint Protection Film in India"}
            </h1>
            <p className="mt-6 max-w-xl text-base text-white/70 sm:text-lg">
              {hero.subheadline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={scrollToEnquiry}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-crimson-500 px-7 py-3.5 text-sm font-semibold shadow-[0_12px_32px_-10px_rgba(245,24,91,0.9)] transition-transform duration-200 hover:scale-105"
              >
                {hero.cta_label || "Enquire Now"}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              {(hero.phones || []).length > 0 && (
                <a href={`tel:${hero.phones[0]}`} className="inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white" data-testid="hero-phone">
                  <Phone className="h-4 w-4 text-crimson-400" /> {hero.phones[0]}
                </a>
              )}
            </div>
            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
              {[
                ["97.5°", "High Gloss"],
                ["5 Yr", "E-Warranty"],
                ["Self", "Healing"],
                ["All", "Weather"],
              ].map(([a, b]) => (
                <div key={b}>
                  <div className="font-display text-2xl font-bold text-crimson-400">{a}</div>
                  <div className="text-xs uppercase tracking-wide text-white/50">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-in relative">
            {hero.image ? (
              <img
                src={mediaUrl(hero.image)}
                alt="Veloskin PPF — glossiest paint protection film in India"
                className="w-full rounded-2xl border border-white/10 shadow-2xl"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width="1264"
                height="848"
                data-testid="hero-image"
              />
            ) : (
              <div className="aspect-[3/4] w-full rounded-2xl border border-white/10 bg-white/5" />
            )}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-crimson-400">What we offer</p>
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Products &amp; Services</h2>
          <p className="mt-3 text-base text-white/60">Premium protection, engineered to perform.</p>
        </div>
        <div className="grid gap-8 lg:grid-cols-3">
          {products.map((p) => {
            const Icon = BRAND_ICON[p.brand] || ShieldCheck;
            return (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-crimson-500/40 hover:bg-white/[0.06]"
              >
                <div className="aspect-[3/4] overflow-hidden bg-[#2A0450]">
                  {p.image ? (
                    <img src={mediaUrl(p.image)} alt={`${p.name} flyer`} loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-white/30">
                      <Icon className="h-16 w-16" />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-1 flex items-center gap-2 text-crimson-400">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{p.brand || "Veloskin"}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold">{p.name}</h3>
                  {p.tagline && <p className="mt-1 text-sm font-medium text-crimson-200">{p.tagline}</p>}
                  <p className="mt-3 text-sm leading-relaxed text-white/65">{p.description}</p>
                  {(p.features || []).length > 0 && (
                    <ul className="mt-5 space-y-2">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-crimson-400" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button onClick={scrollToEnquiry} className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-crimson-400 hover:text-crimson-300">
                    Enquire <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {services.length > 0 && (
          <div className="mt-16" data-testid="services-block">
            <div className="mb-8 max-w-2xl">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-crimson-400">Services we offer</p>
              <h3 className="font-display text-2xl font-bold sm:text-3xl">Professional installation at every accredited studio</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {services.map((s) => (
                <span key={s} data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-sm font-medium text-white/85 transition-colors duration-200 hover:border-crimson-500/40 hover:bg-white/[0.07]">
                  <ShieldCheck className="h-3.5 w-3.5 text-crimson-400" /> {s}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ACADEMY */}
      <section id="academy" className="border-y border-white/10 bg-[#2A0450]/40">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-crimson-500/40 bg-crimson-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-crimson-200">
                <GraduationCap className="h-3.5 w-3.5" /> Training &amp; Certification
              </div>
              {academy.logo ? (
                <>
                  <h2 className="sr-only">Veloskin Detailing Academy</h2>
                  <span className="mb-5 inline-flex rounded-xl bg-white px-4 py-3 shadow-lg">
                    <img src={mediaUrl(academy.logo)} alt="Veloskin Detailing Academy" className="h-12 w-auto" loading="lazy" decoding="async" data-testid="academy-logo" />
                  </span>
                </>
              ) : (
                <h2 className="font-display text-3xl font-bold sm:text-4xl">{academy.title || "Veloskin Detailing Academy"}</h2>
              )}
              {academy.subtitle && <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-crimson-300">{academy.subtitle}</p>}
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">{academy.description}</p>
              <button onClick={scrollToEnquiry} data-testid="academy-cta-btn"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-crimson-500 px-6 py-3 text-sm font-semibold transition-transform duration-200 hover:scale-105">
                Apply to the Academy <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div>
              {academy.image ? (
                <img src={mediaUrl(academy.image)} alt="Veloskin Detailing Academy training" loading="lazy" decoding="async"
                  className="w-full rounded-2xl border border-white/10 object-cover shadow-2xl" data-testid="academy-image" />
              ) : (
                <div className="aspect-video w-full rounded-2xl border border-white/10 bg-white/5" />
              )}
            </div>
          </div>

          {(academy.why || []).length > 0 && (
            <div className="mt-14">
              <h3 className="font-display text-xl font-bold sm:text-2xl">{academy.why_title || "Why train with us"}</h3>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {academy.why.map((w, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-5">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-crimson-400" />
                    <p className="text-sm leading-relaxed text-white/75">{w}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(academy.courses || []).length > 0 && (
            <div className="mt-14">
              <h3 className="font-display text-xl font-bold sm:text-2xl">Our courses</h3>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                {academy.courses.map((c, i) => (
                  <article key={i} data-testid={`academy-course-${i}`}
                    className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors duration-200 hover:border-crimson-500/40">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-lg font-bold">{c.name}</h3>
                      {c.duration && <span className="shrink-0 rounded-full bg-crimson-500/15 px-3 py-1 text-xs font-semibold text-crimson-200">{c.duration}</span>}
                    </div>
                    {c.summary && <p className="mt-2 text-sm leading-relaxed text-white/70">{c.summary}</p>}
                    {(c.bullets || []).length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {c.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-white/80">
                            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-crimson-400" /><span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {c.includes && (
                      <p className="mt-4 text-xs leading-relaxed text-white/55"><span className="font-semibold text-white/70">Includes: </span>{c.includes}</p>
                    )}
                    {c.ideal_for && (
                      <p className="mt-2 text-xs leading-relaxed text-white/55"><span className="font-semibold text-white/70">Ideal for: </span>{c.ideal_for}</p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}

          {academy.closing && (
            <p className="mx-auto mt-12 max-w-3xl text-center text-sm leading-relaxed text-white/60">{academy.closing}</p>
          )}
        </div>
      </section>

      {/* DETAILERS */}
      <section id="detailers" className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-crimson-400">
            <Award className="h-4 w-4" /> Our network
          </p>
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Accredited Detailers</h2>
          <p className="mt-3 text-base text-white/60">Find a Veloskin-accredited studio near you.</p>
        </div>
        {detailers.length === 0 ? (
          <p className="text-white/50">Detailer directory coming soon.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {detailers.map((d) => (
              <div key={d.id} data-testid={`detailer-card-${d.id}`}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-200 hover:border-crimson-500/40">
                <h3 className="font-display text-lg font-semibold">{d.name}</h3>
                {d.city && (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-white/60">
                    <MapPin className="h-3.5 w-3.5 text-crimson-400" /> {d.city}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {d.phone && (
                    <a href={`tel:${d.phone}`} data-testid={`detailer-call-${d.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10">
                      <Phone className="h-3.5 w-3.5" /> {d.phone}
                    </a>
                  )}
                  {d.maps_url && (
                    <a href={d.maps_url} target="_blank" rel="noopener noreferrer" data-testid={`detailer-maps-${d.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-crimson-500/15 px-3 py-1.5 text-xs font-medium text-crimson-200 hover:bg-crimson-500/25">
                      <MapPin className="h-3.5 w-3.5" /> Directions
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ENQUIRY */}
      <section id="enquiry" ref={enquiryRef} className="border-t border-white/10 bg-[#2A0450]/40">
        <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 lg:py-24">
          <div className="mb-10 text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">Contact Us</h2>
            <p className="mt-3 text-white/65">Tell us who you are and we'll reach out shortly.</p>
          </div>
          <EnquiryForm />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 bg-[#1B0730]">
        <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              {hero.logo ? (
                <img src={mediaUrl(hero.logo)} alt="Veloskin PPF" className="h-9 w-auto" loading="lazy" decoding="async" />
              ) : (
                <span className="font-display text-lg font-bold tracking-tight">
                  VELOSKIN <span className="text-crimson-500">PPF</span>
                </span>
              )}
              <p className="mt-4 max-w-xs text-sm text-white/50">Drive protected. Drive with pride. India's glossiest German-technology paint protection film.</p>
            </div>
            <div data-testid="footer-company">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-crimson-400">{company.name || "VeloGuardX by Calgary"}</h3>
              {company.address && (
                <p className="mt-3 flex items-start gap-2 text-sm text-white/65">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-crimson-400" /> {company.address}
                </p>
              )}
              {company.phone && (
                <a href={`tel:${company.phone}`} className="mt-3 inline-flex items-center gap-2 text-sm text-white/65 hover:text-white" data-testid="footer-phone">
                  <Phone className="h-4 w-4 text-crimson-400" /> {company.phone}
                </a>
              )}
            </div>
            <div className="sm:text-right">
              <nav className="flex flex-col gap-2 sm:items-end">
                <a href="#products" className="text-sm text-white/60 hover:text-white">Products</a>
                <a href="#academy" className="text-sm text-white/60 hover:text-white">Academy</a>
                <a href="#detailers" className="text-sm text-white/60 hover:text-white">Detailers</a>
                <a href="#enquiry" className="text-sm text-white/60 hover:text-white">Contact</a>
                <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white">Partner / Staff Login</Link>
              </nav>
            </div>
          </div>
          <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/40">
            © {new Date().getFullYear()} {company.name || "VeloGuardX by Calgary"}. All rights reserved.
          </div>
        </div>
      </footer>

      {loading && (
        <div className="pointer-events-none fixed bottom-4 right-4 flex items-center gap-2 rounded-full bg-black/40 px-3 py-1.5 text-xs text-white/60">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}
    </div>
  );
}

const TYPES = ["Car Owner", "Detailer", "Academy Enquiry"];

function EnquiryForm() {
  const [form, setForm] = useState({ type: "Car Owner", name: "", phone: "", location: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/enquiry", form);
      setDone(true);
      toast.success("Thank you! We've received your enquiry.");
      setForm({ type: "Car Owner", name: "", phone: "", location: "", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-crimson-500/30 bg-crimson-500/10 p-10 text-center" data-testid="enquiry-success">
        <ShieldCheck className="mx-auto h-12 w-12 text-crimson-400" />
        <h3 className="mt-4 font-display text-xl font-bold">Enquiry received!</h3>
        <p className="mt-2 text-sm text-white/70">Our team will contact you soon. Thank you for choosing Veloskin.</p>
        <button onClick={() => setDone(false)} className="mt-5 text-sm font-semibold text-crimson-400 hover:text-crimson-300">
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8" data-testid="enquiry-form">
      <label className="mb-2 block text-sm font-medium text-white/80">I am a…</label>
      <div className="mb-5 grid grid-cols-3 gap-2">
        {TYPES.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`enquiry-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setForm((f) => ({ ...f, type: t }))}
            className={`rounded-lg border px-2 py-2.5 text-xs font-semibold transition-colors duration-200 sm:text-sm ${
              form.type === t
                ? "border-crimson-500 bg-crimson-500 text-white"
                : "border-white/15 bg-transparent text-white/70 hover:bg-white/5"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-white/80">Name</label>
          <Input value={form.name} onChange={set("name")} placeholder="Your full name"
            data-testid="enquiry-name" className="border-white/15 bg-white/5 text-white placeholder:text-white/30" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-white/80">Phone</label>
          <Input value={form.phone} onChange={set("phone")} placeholder="Mobile number"
            data-testid="enquiry-phone" className="border-white/15 bg-white/5 text-white placeholder:text-white/30" />
        </div>
      </div>
      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-white/80">Location</label>
        <Input value={form.location} onChange={set("location")} placeholder="City / area"
          data-testid="enquiry-location" className="border-white/15 bg-white/5 text-white placeholder:text-white/30" />
      </div>
      <div className="mt-4">
        <label className="mb-1.5 block text-sm font-medium text-white/80">Message</label>
        <Textarea value={form.message} onChange={set("message")} placeholder="Tell us what you need"
          rows={4} data-testid="enquiry-message" className="border-white/15 bg-white/5 text-white placeholder:text-white/30" />
      </div>
      <Button type="submit" disabled={busy} data-testid="enquiry-submit"
        className="mt-6 w-full bg-crimson-500 py-6 text-sm font-semibold hover:bg-crimson-600">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Enquiry"}
      </Button>
    </form>
  );
}
